import { clsx } from 'clsx'
import { motion } from 'motion/react'
import { Fragment, useState } from 'react'

import styles from 'components/pages/AdminSettingsPage.module.css'
import { Button } from 'components/ui/Button'
import { StatusBadge } from 'components/ui/StatusBadge'
import { useAdminFilesQuery, useAdminUsersActivityQuery } from 'modules/admin/queries'

const ENTRANCE_OFFSET_PX = 14
const ENTRANCE_DURATION_SECONDS = 0.28
const ID_PREVIEW_LENGTH = 8

export const AdminSettingsScreen = () => {
  /* --- Section 1: Hooks --- */
  const { data: adminFiles = [], error, isFetching, refetch } = useAdminFilesQuery()
  const {
    data: usersActivity = [],
    error: usersActivityError,
    isFetching: usersActivityFetching,
    refetch: refetchUsersActivity,
  } = useAdminUsersActivityQuery()

  /* --- Section 2: Local State --- */
  const [expandedUserIds, setExpandedUserIds] = useState<ReadonlySet<string>>(() => new Set())

  /* --- Section 4: Event Handlers --- */
  const handleToggleUserExpanded = (userId: string) => {
    setExpandedUserIds(previous => {
      const next = new Set(previous)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }

      return next
    })
  }

  /* --- Section 6: Render --- */
  return (
    <div className={clsx('athlete-app', styles.adminSettingsPage)}>
      <motion.section
        className={styles.adminSettingsPanel}
        initial={{ opacity: 0, y: ENTRANCE_OFFSET_PX }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ENTRANCE_DURATION_SECONDS, ease: 'easeOut' }}
      >
        <h1 className={styles.adminSettingsTitle}>Admin Settings</h1>

        <div className={clsx(styles.adminSettingsGroupTitle, styles.adminSettingsGroupTitleUsers)}>User activity</div>
        <p className={styles.adminUsersIntro}>
          Firebase user IDs, video uploads (file records), and message counts for each conversation.
        </p>

        <div className={styles.adminFilesSection}>
          <Button
            className={styles.adminSecondaryChip}
            disabled={usersActivityFetching}
            onClick={() => {
              void refetchUsersActivity()
            }}
            size="sm"
            variant="secondary"
          >
            {usersActivityFetching ? 'Loading...' : 'Refresh'}
          </Button>

          {usersActivityError ? (
            <p className={clsx(styles.adminFilesFeedback, styles.adminFilesFeedbackError)}>
              {usersActivityError instanceof Error ? usersActivityError.message : 'Failed to load user activity'}
            </p>
          ) : null}

          {usersActivity.length === 0 && !usersActivityFetching ? (
            <p className={styles.adminFilesFeedback}>No users with files or conversations yet.</p>
          ) : (
            <div className={styles.adminFilesTableWrapper}>
              <table className={styles.adminFilesTable}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Videos uploaded</th>
                    <th>Conversations</th>
                    <th aria-label="Expand" />
                  </tr>
                </thead>
                <tbody>
                  {usersActivity.map(user => {
                    const isOpen = expandedUserIds.has(user.user_id)
                    return (
                      <Fragment key={user.user_id}>
                        <tr>
                          <td className={styles.adminFilesMono}>{user.user_id}</td>
                          <td>{user.videos_uploaded}</td>
                          <td>{user.conversations.length}</td>
                          <td className={styles.adminUsersExpandCell}>
                            <Button
                              className={styles.adminUsersExpandButton}
                              onClick={() => {
                                handleToggleUserExpanded(user.user_id)
                              }}
                              size="sm"
                              type="button"
                              variant="secondary"
                            >
                              {isOpen ? 'Hide' : 'Details'}
                            </Button>
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr className={styles.adminUsersDetailRow}>
                            <td colSpan={4}>
                              {user.conversations.length === 0 ? (
                                <p className={styles.adminUsersNestedEmpty}>No conversations for this user.</p>
                              ) : (
                                <div className={styles.adminUsersNestedWrap}>
                                  <table className={clsx(styles.adminFilesTable, styles.adminUsersNestedTable)}>
                                    <thead>
                                      <tr>
                                        <th>Conversation</th>
                                        <th>Title</th>
                                        <th>Messages</th>
                                        <th>Updated</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {user.conversations.map(conversation => (
                                        <tr key={conversation.id}>
                                          <td className={styles.adminFilesMono}>
                                            {conversation.id.slice(0, ID_PREVIEW_LENGTH)}…
                                          </td>
                                          <td>{conversation.title ?? '—'}</td>
                                          <td>{conversation.message_count}</td>
                                          <td className={styles.adminFilesCellCompact}>
                                            {new Date(conversation.updated_at).toLocaleString()}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={clsx(styles.adminSettingsGroupTitle, styles.adminSettingsGroupTitleFiles)}>Files Inspector</div>

        <div className={styles.adminFilesSection}>
          <Button
            className={styles.adminSecondaryChip}
            disabled={isFetching}
            onClick={() => {
              refetch()
            }}
            size="sm"
            variant="secondary"
          >
            {isFetching ? 'Loading...' : 'Refresh'}
          </Button>

          {error ? (
            <p className={clsx(styles.adminFilesFeedback, styles.adminFilesFeedbackError)}>
              {error instanceof Error ? error.message : 'Failed to load files'}
            </p>
          ) : null}

          {adminFiles.length === 0 && !isFetching ? (
            <p className={styles.adminFilesFeedback}>No files found.</p>
          ) : (
            <div className={styles.adminFilesTableWrapper}>
              <table className={styles.adminFilesTable}>
                <thead>
                  <tr>
                    <th>File ID</th>
                    <th>Processing Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {adminFiles.map(file => (
                    <tr key={file.id}>
                      <td className={styles.adminFilesMono}>{file.id.slice(0, ID_PREVIEW_LENGTH)}…</td>
                      <td>
                        <StatusBadge
                          className={clsx(
                            styles.adminFilesBadge,
                            styles[`adminFilesBadge--${file.processing_status ?? 'missing'}`]
                          )}
                          status={file.processing_status ?? 'missing'}
                        >
                          {file.processing_status ?? '—'}
                        </StatusBadge>
                      </td>
                      <td className={styles.adminFilesCellCompact}>{new Date(file.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.section>
    </div>
  )
}
